import WorkflowItemDetailsSection from 'src/components/WorkflowTaskDetailDrawer/sections/Details'
import WorkflowTaskDetailDrawer from 'src/components/WorkflowTaskDetailDrawer/WorkflowTaskDetailDrawer'
import useQuery from 'src/hooks/useQuery'
import {
  UPDATE_CUSTOM_WORK_ITEM_STATUS,
  CREATE_LABOR_TRANSACTION,
  GET_WORK_ORDER_CUSTOM_TASK_DETAILS,
  GET_WORK_ORDER_BASE_FIELDS,
} from '../../queries'
import WorkPerformedSection from '../sections/WorkPerformedSection'
import { useMutation } from '@cedarjs/web'
import AttachmentsSection from '../sections/AttachmentsSection'
import Button from 'src/components/MUI/Button'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import useHasPermission from 'src/hooks/useHasPermission'
import { Permissions } from '@wingwork/common/src/constants/permissions'
import useModal from 'src/hooks/useModal'
import { useAuth } from 'src/auth'
import Divider from '@mui/material/Divider'
import ComplianceEventLogsModal from 'src/components/ComplianceEventLogsModal'
import LaborTransactionsModal from 'src/components/LaborTransactionsModal'
import SaveLaborTransactionModal from 'src/components/SaveLaborTransactionModal'
import { CustomInternalWorkItem } from 'types/graphql'
import { useApolloClient } from '@apollo/client'
import LaborDetailsSection from '../sections/LaborDetailsSection'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import ConstructionIcon from '@mui/icons-material/Construction'
import useWorkOrderVitals from 'src/hooks/useWorkOrderVitals'
import { useMemo } from 'react'
interface CustomWorkItemDetailsProps {
  workOrderId: string
  onBack?: () => void
  onNext?: () => void
}

const CustomWorkItemDetails = ({
  workOrderId,
  onBack,
  onNext,
}: CustomWorkItemDetailsProps) => {
  const client = useApolloClient()
  const {
    workOrderState: { detailDrawerCustomTaskId },
  } = useWorkOrderVitals()
  const { currentUser } = useAuth()
  const { handleOpen: handleOpenComplianceEventLogs } = useModal(
    'complianceEventLogsModal'
  )
  const { handleOpen: handleOpenLaborTransactions } = useModal(
    'laborTransactionsModal'
  )
  const { handleOpen: handleOpenSaveLaborTransactionModal } = useModal(
    'saveLaborTransactionModal'
  )

  const {
    data: taskDetails,
    refetch: refetchTaskDetails,
    loading: taskDetailsLoading,
  } = useQuery(GET_WORK_ORDER_CUSTOM_TASK_DETAILS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: detailDrawerCustomTaskId,
    },
    skip: !detailDrawerCustomTaskId,
  })

  const { data: workOrderBaseFields } = useQuery(GET_WORK_ORDER_BASE_FIELDS, {
    variables: { id: workOrderId },
  })
  const aircraftUsageLogId =
    workOrderBaseFields?.internalWorkOrder?.aircraftUsageLogId

  const [updateCustomWorkItemStatus, { loading: updateWorkItemStatusLoading }] =
    useMutation(UPDATE_CUSTOM_WORK_ITEM_STATUS)
  const [createLaborTransaction] = useMutation(CREATE_LABOR_TRANSACTION)

  const aircraftId =
    taskDetails?.customInternalWorkItem?.maintenanceItem?.aircraft?.id

  const currentUserInProgressLaborTransaction =
    taskDetails?.customInternalWorkItem?.laborTransactions?.find((item) => {
      return (
        item.punchedOutAt === null &&
        item.workedBy?.clerkId === currentUser?.providerId
      )
    }) ?? null

  const isComplianceInProgressByCurrentUser =
    currentUserInProgressLaborTransaction !== null

  const canUpdateWorkOrder = useHasPermission(
    Permissions.workOrder.update,
    aircraftId
  )
  const canReadMtxItem = useHasPermission(
    Permissions.maintenanceItem.read,
    aircraftId
  )
  const canPerformWorkItem = useHasPermission(
    Permissions.workOrder.performWorkItem,
    aircraftId
  )

  const toggleLaborTransaction = async () => {
    if (isComplianceInProgressByCurrentUser) {
      handleOpenSaveLaborTransactionModal({
        laborTransaction: currentUserInProgressLaborTransaction,
        variant: 'punchOut',
      })
    } else {
      // If compliance is not in progress, start it
      await createLaborTransaction({
        variables: {
          id: '',
          input: {
            punchedInAt: new Date().toISOString(),
            customInternalWorkItemId: detailDrawerCustomTaskId,
          },
        },
        update: (cache, { data: mutationData }) => {
          cache.modify({
            id: client.cache.identify({
              __typename: 'CustomInternalWorkItem',
              id: detailDrawerCustomTaskId,
            }),
            fields: {
              laborTransactions: () => [
                ...taskDetails?.customInternalWorkItem?.laborTransactions,
                mutationData.upsertLaborTransaction,
              ],
            },
          })
        },
      })
      if (taskDetails?.customInternalWorkItem?.status === 'REVIEW') {
        await updateCustomWorkItemStatus({
          variables: { id: detailDrawerCustomTaskId, status: 'IN_PROGRESS' },
        })
      }
    }
  }

  // Consider it loading if we're either in a loading state OR if we don't have data for the current ID
  const isLoading =
    taskDetailsLoading ||
    (detailDrawerCustomTaskId && !taskDetails?.customInternalWorkItem)
  const isTaskInReview =
    taskDetails?.customInternalWorkItem?.status === 'REVIEW'
  const isTaskInProgress =
    taskDetails?.customInternalWorkItem?.laborTransactions?.find(
      (item) => !item.punchedOutAt
    )
      ? true
      : false

  const taskStatus = useMemo(() => {
    if (!aircraftUsageLogId) {
      return 'APPLY_TIMES'
    }
    if (isTaskInProgress) {
      return 'IN_PROGRESS_LABOR_TRANSACTION'
    }
    if (isTaskInReview) {
      return 'COMPLETE'
    }
    return 'READY_TO_COMPLETE'
  }, [aircraftUsageLogId, isTaskInProgress, isTaskInReview])

  return (
    <WorkflowTaskDetailDrawer
      isWorkOrder
      isCustomWorkItem
      open={!!detailDrawerCustomTaskId}
      aircraftId={aircraftId}
      showNextItemActions
      title={taskDetails?.customInternalWorkItem?.title}
      loading={isLoading}
      onBack={onBack}
      onNext={onNext}
      onComplete={() => {
        updateCustomWorkItemStatus({
          variables: { id: detailDrawerCustomTaskId, status: 'REVIEW' },
        })
      }}
      taskStatus={taskStatus}
      completeLoading={updateWorkItemStatusLoading || taskDetailsLoading}
    >
      {({ flashSaved, flashError }) => (
        <>
          <div className="flex flex-col gap-2">
            <div>
              <div className="flex justify-between gap-1 p-2">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    isComplianceInProgressByCurrentUser ? (
                      <PendingActionsOutlinedIcon />
                    ) : (
                      <TimerOutlinedIcon />
                    )
                  }
                  onClick={toggleLaborTransaction}
                  locked={!canPerformWorkItem}
                  lockedTooltip="You do not have permission"
                  color={
                    isComplianceInProgressByCurrentUser ? 'warning' : 'primary'
                  }
                >
                  {isComplianceInProgressByCurrentUser
                    ? 'CLOCK OUT'
                    : 'CLOCK IN'}
                </Button>
                <div className="flex gap-1">
                  <Button
                    variant="outlined"
                    color="base"
                    size="small"
                    startIcon={<MenuBookIcon fontSize="small" />}
                    onClick={() => {
                      handleOpenComplianceEventLogs({
                        targetId: detailDrawerCustomTaskId,
                        discriminator: 'CustomInternalWorkItem',
                      })
                    }}
                  >
                    TASK ACTIVITY
                  </Button>
                  <Button
                    variant="outlined"
                    color="base"
                    onClick={() => {
                      handleOpenLaborTransactions({
                        targetId: detailDrawerCustomTaskId,
                        discriminator: 'CustomInternalWorkItem',
                      })
                    }}
                    size="small"
                    className="rounded border border-solid"
                    startIcon={<ConstructionIcon fontSize="small" />}
                  >
                    LABOR ACTIVITY
                  </Button>
                </div>
              </div>
              <Divider />
            </div>
            <WorkflowItemDetailsSection
              task={taskDetails?.customInternalWorkItem}
              loading={isLoading}
            />
            <WorkPerformedSection
              id={detailDrawerCustomTaskId}
              workItem={taskDetails?.customInternalWorkItem}
              isCustomWorkItem={true}
              flashSaved={flashSaved}
              flashError={flashError}
            />
            <LaborDetailsSection
              task={taskDetails?.customInternalWorkItem}
              refetch={() => {
                refetchTaskDetails({
                  id: detailDrawerCustomTaskId,
                })
                  .then(() => {
                    flashSaved()
                  })
                  .catch(() => {
                    flashError()
                  })
              }}
            />
            <AttachmentsSection
              workItemId={detailDrawerCustomTaskId}
              isCustomWorkItem={true}
              flashSaved={flashSaved}
              flashError={flashError}
            />
          </div>
          <ComplianceEventLogsModal />
          <LaborTransactionsModal />
          <SaveLaborTransactionModal
            refetch={() => {
              refetchTaskDetails({
                id: detailDrawerCustomTaskId,
              })
                .then(() => {
                  flashSaved()
                })
                .catch(() => {
                  flashError()
                })
            }}
          />
        </>
      )}
    </WorkflowTaskDetailDrawer>
  )
}

export default CustomWorkItemDetails
